import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const http = axios.create({ baseURL: API, timeout: 20000, withCredentials: true });

// Attach bearer token if we have it in localStorage (fallback for cookie-less browsers).
http.interceptors.request.use((config) => {
  const tok = localStorage.getItem('pdxmap_session');
  if (tok) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${tok}`;
  }
  return config;
});

export async function getStartups() {
  const { data } = await http.get('/startups');
  return data.startups || [];
}

export async function getStartup(id) {
  const { data } = await http.get(`/startups/${id}`);
  return data;
}

export async function sendChat({ message, sessionId }) {
  const { data } = await http.post('/chat', { message, session_id: sessionId }, { timeout: 90000 });
  return data; // { session_id, reply }
}

export async function submitStartup(payload) {
  const { data } = await http.post('/startups/submissions', payload);
  return data;
}

export async function submitFeedback(payload) {
  const { data } = await http.post('/feedback', payload);
  return data;
}

// ---- Auth ----
export async function authExchangeSession(sessionId) {
  const { data } = await http.post('/auth/session', null, {
    headers: { 'X-Session-ID': sessionId },
  });
  if (data.session_token) {
    localStorage.setItem('pdxmap_session', data.session_token);
  }
  return data.user;
}

export async function authMe() {
  try {
    const { data } = await http.get('/auth/me');
    return data;
  } catch (e) {
    if (e?.response?.status === 401) return null;
    throw e;
  }
}

export async function authLogout() {
  try { await http.post('/auth/logout'); } catch (e) { /* ignore */ }
  localStorage.removeItem('pdxmap_session');
}

// ---- Claim / edit startup ----
export async function claimStartup(startupId) {
  const { data } = await http.post(`/startups/${startupId}/claim`);
  return data;
}

export async function updateStartup(startupId, patch) {
  const { data } = await http.patch(`/startups/${startupId}`, patch);
  return data.startup;
}

export async function getEvents() {
  const { data } = await http.get('/events', { params: { upcoming: true } });
  return data.events || [];
}

export async function getTechEvents(days = 60) {
  const { data } = await http.get('/tech-events', { params: { days } });
  return data.events || [];
}

export async function getNeighborhoods() {
  const { data } = await http.get('/neighborhoods');
  return data.neighborhoods || [];
}

// ---- Admin ----
function adminHeaders(token) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

export async function adminVerify(token) {
  await http.post('/admin/verify', null, adminHeaders(token));
  return true;
}

export async function adminListSubmissions(token, status) {
  const { data } = await http.get('/admin/submissions', {
    ...adminHeaders(token),
    params: status ? { status } : {},
  });
  return data.submissions || [];
}

export async function adminApproveSubmission(token, id, body = {}) {
  const { data } = await http.post(`/admin/submissions/${id}/approve`, body, adminHeaders(token));
  return data;
}

export async function adminRejectSubmission(token, id) {
  const { data } = await http.post(`/admin/submissions/${id}/reject`, null, adminHeaders(token));
  return data;
}

// ---- Admin: URL audit ----
export async function adminGetLatestAudit(token) {
  const { data } = await http.get('/admin/audit/latest', adminHeaders(token));
  return data.run;
}

export async function adminListAuditRuns(token, limit = 10) {
  const { data } = await http.get('/admin/audit/runs', {
    ...adminHeaders(token),
    params: { limit },
  });
  return data.runs || [];
}

export async function adminRunAudit(token, deleteBroken = true) {
  const { data } = await http.post('/admin/audit/run', null, {
    ...adminHeaders(token),
    params: { delete_broken: deleteBroken },
    timeout: 180000,
  });
  return data;
}

// ---- Admin: Feedback ----
export async function adminListFeedback(token, status) {
  const { data } = await http.get('/admin/feedback', {
    ...adminHeaders(token),
    params: status ? { status } : {},
  });
  return data.feedback || [];
}

export async function adminSetFeedbackStatus(token, id, status) {
  const { data } = await http.post(`/admin/feedback/${id}/status`, null, {
    ...adminHeaders(token),
    params: { status },
  });
  return data;
}
